import React, { useState, memo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getToken } from "../../utils/auth";
import CONFIG from "../../config";
import { sendContractNotification } from "../../services/notificationService";
import { Helmet } from "react-helmet-async";
import SEO from "../../components/common/SEO";

const ContractAdmin = memo(() => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');
  const [step, setStep] = useState("buyer-info");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [createdData, setCreatedData] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [domainAvailable, setDomainAvailable] = useState(null);

  const [buyerInfo, setBuyerInfo] = useState({
    buyerName: "",
    buyerPhone: "",
    buyerWhatsapp: "",
    buyerEmail: "",
    buyerPassword: "",
    domainName: "",
    storeName: "",
    contractNumber: "",
    contractDate: new Date().toISOString().split('T')[0],
    paymentType: "monthly",
    monthlyFee: "",
    oneTimeFee: "",
    currency: "USD",
    databaseProvider: "default",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBuyerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const checkDomainAvailability = async (domain) => {
    if (!domain) {
      setDomainAvailable(null);
      return;
    }

    setCheckingDomain(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${CONFIG.API_URL}/api/admin/configs/profiles?search_hostname=${encodeURIComponent(domain)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDomainAvailable(!res.data || res.data.length === 0);
    } catch (err) {
      setDomainAvailable(true);
    } finally {
      setCheckingDomain(false);
    }
  };

  const handleDomainChange = (e) => {
    const domain = e.target.value;
    handleInputChange(e);
    checkDomainAvailability(domain);
  };

  const handleReviewStep = () => {
    if (!buyerInfo.buyerName || !buyerInfo.buyerEmail || !buyerInfo.domainName || !buyerInfo.storeName) {
      setMessage(t('contract.validation_error', 'Please fill in all required fields'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerInfo.buyerEmail)) {
      setMessage(t('contract.invalid_email', 'Invalid email format'));
      return;
    }

    if (domainAvailable === false) {
      setMessage(t('contract.domain_taken', 'This domain name is already taken. Please choose another.'));
      return;
    }

    if (buyerInfo.paymentType === "monthly" && !buyerInfo.monthlyFee) {
      setMessage(t('contract.fee_required', 'Please enter monthly fee'));
      return;
    }

    if (buyerInfo.paymentType === "one-time" && !buyerInfo.oneTimeFee) {
      setMessage(t('contract.fee_required', 'Please enter one-time fee'));
      return;
    }

    setMessage("");
    setStep("review");
  };

  const handleCreateContract = async () => {
    setLoading(true);
    setMessage("");

    try {
      const token = getToken();
      const tempPassword = buyerInfo.buyerPassword || Math.random().toString(36).slice(-12);

      const profileRes = await axios.post(
        `${CONFIG.API_URL}/api/admin/configs/profiles`,
        {
          name: buyerInfo.storeName,
          description: `Store for ${buyerInfo.buyerName}`,
          hostname: buyerInfo.domainName,
          is_active: 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Create isolated database for the new buyer based on selected provider
      try {
        await axios.post(
          `${CONFIG.API_URL}/api/admin/databases/create`,
          {
            profileId: profileRes.data.id,
            provider: buyerInfo.databaseProvider,
            dbName: `${buyerInfo.storeName.replace(/\s+/g, "_").toLowerCase()}_db`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (dbErr) {
        console.error('Database creation failed:', dbErr);
        // Continue without blocking contract creation; inform admin via UI later if needed
      }

      const profileId = profileRes.data.id;

      // Create isolated buyer database for the new profile
      const generateDatabaseUrl = (storeName, provider = 'default') => {
        const dbName = storeName.replace(/\s+/g, '_').toLowerCase() + '_db';
        const providers = {
          default: {
            user: 'buyer_user',
            pass: 'buyer_pass',
            host: 'db.example.com',
          },
          aiven: {
            user: 'aiven_user',
            pass: 'aiven_pass',
            host: 'aws-us-east-1.aivencloud.com',
          },
          mariadb: {
            user: 'mariadb_user',
            pass: 'mariadb_pass',
            host: 'db.mariadb.example.com',
          },
        };
        const config = providers[provider] || providers.default;
        return `mysql://${config.user}:${config.pass}@${config.host}/${dbName}`;
      };

      try {
        const dbUrl = generateDatabaseUrl(buyerInfo.storeName);
        // Set database_url in config_profiles (via profileRoutes)
        try {
          await axios.put(
            `${CONFIG.API_URL}/api/profiles/${profileId}/database`,
            { database_url: dbUrl },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (dbErr) {
          console.error('Database creation error (non-blocking):', dbErr);
        }

        // Set the DATABASE_URL in app_configs for the profile
        try {
          await axios.post(
            `${CONFIG.API_URL}/api/admin/configs`,
            {
              profile_id: profileId,
              config_key: 'DATABASE_URL',
              config_value: dbUrl,
              is_active: 1,
              description: 'Database URL for the buyer store'
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (configErr) {
          console.warn('Failed to set DATABASE_URL config for profile (non-blocking):', configErr);
        }

      } catch (dbErr) {
        console.error('Database creation error (non-blocking):', dbErr);
      }

      const userRes = await axios.post(
        `${CONFIG.API_URL}/api/admin/users`,
        {
          username: buyerInfo.buyerEmail.split('@')[0],
          fullname: buyerInfo.buyerName,
          email: buyerInfo.buyerEmail,
          phone: buyerInfo.buyerPhone,
          password: tempPassword,
          isAdmin: true,
          is_master_admin: false,
          permissions: ['*'],
          profile_id: profileId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      try {
        await sendContractNotification({
          buyerName: buyerInfo.buyerName,
          buyerEmail: buyerInfo.buyerEmail,
          buyerPhone: buyerInfo.buyerPhone,
          buyerWhatsapp: buyerInfo.buyerWhatsapp,
          username: buyerInfo.buyerEmail.split('@')[0],
          tempPassword: tempPassword,
          domainName: buyerInfo.domainName,
          storeName: buyerInfo.storeName,
          contractNumber: buyerInfo.contractNumber,
        });
      } catch (notifErr) {
        console.error("Notification error (non-blocking):", notifErr);
      }

      setCreatedData({
        profile: profileRes.data,
        user: userRes.data,
        buyerInfo: buyerInfo,
        tempPassword: tempPassword
      });

      setMessage(t('contract.success', 'Contract created and credentials sent!'));
      setStep("success");
    } catch (err) {
      console.error(err);
      let errMsg = err.response?.data?.message || err.message || "Failed to create contract";

      if (errMsg.includes("Duplicate entry") && errMsg.includes("ux_hostname")) {
        errMsg = t('contract.domain_taken', 'This domain name is already taken. Please choose another.');
      }

      setMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBuyerInfo({
      buyerName: "",
      buyerPhone: "",
      buyerWhatsapp: "",
      buyerEmail: "",
      buyerPassword: "",
      domainName: "",
      storeName: "",
      contractNumber: "",
      contractDate: new Date().toISOString().split('T')[0],
      paymentType: "monthly",
      monthlyFee: "",
      oneTimeFee: "",
      currency: "USD",
      databaseProvider: "default",
    });
    setStep("buyer-info");
    setMessage("");
    setCreatedData(null);
    setDomainAvailable(null);
  };

  const downloadPDF = () => {
    const content = `CONTRACT AGREEMENT

Contract Number: ${buyerInfo.contractNumber || 'N/A'}
Date: ${buyerInfo.contractDate}

PARTIES:
Supplier: MyApp Platform
Buyer: ${buyerInfo.buyerName}
Email: ${buyerInfo.buyerEmail}
Phone: ${buyerInfo.buyerPhone || 'N/A'}

STORE DETAILS:
Store Name: ${buyerInfo.storeName}
Domain: ${buyerInfo.domainName}

PAYMENT TERMS:
Type: ${buyerInfo.paymentType === 'monthly' ? 'Monthly Subscription' : 'One-Time Payment'}
Fee: ${buyerInfo.paymentType === 'monthly' ? buyerInfo.monthlyFee : buyerInfo.oneTimeFee} ${buyerInfo.currency}

This agreement confirms the above terms and conditions.`;

    const element = document.createElement("a");
    element.setAttribute("href", "data:text/plain;charset=utf-8," + encodeURIComponent(content));
    element.setAttribute("download", `contract_${buyerInfo.contractNumber || 'draft'}.txt`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <>
      <Helmet>
        <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
      </Helmet>
      <SEO />
      <div className="max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-black text-gray-900 tracking-tight mb-4">
            {t('contract.management', 'Contract Management')}
          </h1>
          <p className="text-gray-500 font-medium text-lg">
            {t('contract.subtitle', 'Create service contracts and set up buyer accounts')}
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-2xl font-medium ${message.includes('successfully') || message.includes('تم')
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
            {message}
          </div>
        )}

        {/* Step Indicator */}
        <div className="mb-12 flex items-center justify-center gap-4">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold ${step === 'buyer-info' || step === 'review' || step === 'success'
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-200 text-gray-600'
            }`}>
            1
          </div>
          <div className="flex-1 h-1 bg-gray-200"></div>
          <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold ${step === 'review' || step === 'success'
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-200 text-gray-600'
            }`}>
            2
          </div>
          <div className="flex-1 h-1 bg-gray-200"></div>
          <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold ${step === 'success'
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-200 text-gray-600'
            }`}>
            3
          </div>
        </div>

        {/* Step 1: Buyer Info Form */}
        {step === "buyer-info" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Buyer Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.buyer_name', 'Buyer Name')} *
                </label>
                <input
                  type="text"
                  name="buyerName"
                  value={buyerInfo.buyerName}
                  onChange={handleInputChange}
                  placeholder={t('contract.buyer_name', 'Buyer Name')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Buyer Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.buyer_email', 'Buyer Email')} *
                </label>
                <input
                  type="email"
                  name="buyerEmail"
                  value={buyerInfo.buyerEmail}
                  onChange={handleInputChange}
                  placeholder={t('contract.buyer_email', 'Buyer Email')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Buyer Password */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.buyer_password', 'Login Password')}
                  <span className="text-xs text-gray-500 font-normal">(optional - leave empty for auto-generated)</span>
                </label>
                <input
                  type="password"
                  name="buyerPassword"
                  value={buyerInfo.buyerPassword}
                  onChange={handleInputChange}
                  placeholder={t('contract.buyer_password', 'Leave empty for auto-generated password')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Buyer Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.buyer_phone', 'Phone Number')}
                </label>
                <input
                  type="tel"
                  name="buyerPhone"
                  value={buyerInfo.buyerPhone}
                  onChange={handleInputChange}
                  placeholder="+1234567890"
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Buyer WhatsApp */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.buyer_whatsapp', 'WhatsApp Number')}
                </label>
                <input
                  type="tel"
                  name="buyerWhatsapp"
                  value={buyerInfo.buyerWhatsapp}
                  onChange={handleInputChange}
                  placeholder="+1234567890"
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Store Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.store_name', 'Store Name')} *
                </label>
                <input
                  type="text"
                  name="storeName"
                  value={buyerInfo.storeName}
                  onChange={handleInputChange}
                  placeholder={t('contract.store_name', 'Store Name')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Domain Name with availability check */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.domain_name', 'Domain Name')} * <span className="text-xs text-gray-500">(e.g., example.com)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="domainName"
                    value={buyerInfo.domainName}
                    onChange={handleDomainChange}
                    placeholder="example.com"
                    className={`w-full px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 ${domainAvailable === true ? 'border-green-300 focus:border-green-500' :
                      domainAvailable === false ? 'border-red-300 focus:border-red-500' :
                        'border-gray-300 focus:border-indigo-500'
                      }`}
                  />
                  {checkingDomain && <div className="absolute right-4 top-3 text-gray-400 text-sm">Checking...</div>}
                  {domainAvailable === true && <div className="absolute right-4 top-3 text-green-600 font-bold">✓</div>}
                  {domainAvailable === false && <div className="absolute right-4 top-3 text-red-600 font-bold">✗</div>}
                </div>
                {domainAvailable === false && (
                  <p className="text-red-600 text-xs mt-1">{t('contract.domain_taken', 'Domain is already taken')}</p>
                )}
              </div>

              {/* Contract Number */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.contract_number', 'Contract Number')}
                </label>
                <input
                  type="text"
                  name="contractNumber"
                  value={buyerInfo.contractNumber}
                  onChange={handleInputChange}
                  placeholder="2024-001"
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Contract Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.contract_date', 'Contract Date')}
                </label>
                <input
                  type="date"
                  name="contractDate"
                  value={buyerInfo.contractDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                />
              </div>

              {/* Payment Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.payment_type', 'Payment Type')} *
                </label>
                <select
                  name="paymentType"
                  value={buyerInfo.paymentType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                >
                  <option value="monthly">{t('contract.monthly_subscription', 'Monthly Subscription')}</option>
                  <option value="one-time">{t('contract.one_time_payment', 'One-Time Payment')}</option>
                </select>
              </div>

              {/* Monthly Fee (shown only if monthly) */}
              {buyerInfo.paymentType === 'monthly' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {t('contract.monthly_fee', 'Monthly Fee')} *
                  </label>
                  <input
                    type="number"
                    name="monthlyFee"
                    value={buyerInfo.monthlyFee}
                    onChange={handleInputChange}
                    placeholder="99.99"
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                  />
                </div>
              )}

              {/* One-Time Fee (shown only if one-time) */}
              {buyerInfo.paymentType === 'one-time' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {t('contract.one_time_fee', 'One-Time Fee')} *
                  </label>
                  <input
                    type="number"
                    name="oneTimeFee"
                    value={buyerInfo.oneTimeFee}
                    onChange={handleInputChange}
                    placeholder="299.99"
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                  />
                </div>
              )}

              {/* Currency */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.currency', 'Currency')}
                </label>
                <select
                  name="currency"
                  value={buyerInfo.currency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="EGP">EGP (£)</option>
                </select>
              </div>

              {/* Database Provider */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('contract.database_provider', 'Database Provider')} *
                </label>
                <select
                  name="databaseProvider"
                  value={buyerInfo.databaseProvider}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                >
                  <option value="default">{t('contract.db_default', 'Default MySQL')}</option>
                  <option value="aiven">{t('contract.db_aiven', 'Aiven Cloud SQL')}</option>
                  <option value="mariadb">{t('contract.db_mariadb', 'MariaDB')}</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end mt-8">
              <button
                onClick={handleReset}
                className="px-8 py-3 border border-gray-300 rounded-2xl font-bold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('contract.reset', 'Reset')}
              </button>
              <button
                onClick={handleReviewStep}
                disabled={domainAvailable === false || domainAvailable === null}
                className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('contract.next', 'Next')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review & Contract Display */}
        {step === "review" && (
          <div className="space-y-8">
            {/* Contract Preview */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contract.preview', 'Contract Preview')}</h2>
              <div className="bg-white p-6 rounded-xl text-sm text-gray-700 space-y-4 max-h-96 overflow-y-auto">
                <div>
                  <strong>{t('contract.contract_number', 'Contract Number')}:</strong> {buyerInfo.contractNumber || 'N/A'}
                </div>
                <div>
                  <strong>{t('contract.contract_date', 'Contract Date')}:</strong> {buyerInfo.contractDate}
                </div>
                <div>
                  <strong>{t('contract.buyer_name', 'Buyer Name')}:</strong> {buyerInfo.buyerName}
                </div>
                <div>
                  <strong>{t('contract.buyer_email', 'Buyer Email')}:</strong> {buyerInfo.buyerEmail}
                </div>
                <div>
                  <strong>{t('contract.buyer_password', 'Login Password')}:</strong> {buyerInfo.buyerPassword ? '••••••••' : '(auto-generated)'}
                </div>
                <div>
                  <strong>{t('contract.buyer_phone', 'Phone')}:</strong> {buyerInfo.buyerPhone || 'N/A'}
                </div>
                <div>
                  <strong>{t('contract.buyer_whatsapp', 'WhatsApp')}:</strong> {buyerInfo.buyerWhatsapp || 'N/A'}
                </div>
                <div>
                  <strong>{t('contract.store_name', 'Store Name')}:</strong> {buyerInfo.storeName}
                </div>
                <div>
                  <strong>{t('contract.domain_name', 'Domain Name')}:</strong> {buyerInfo.domainName}
                </div>
                <div>
                  <strong>{t('contract.payment_type', 'Payment Type')}:</strong> {buyerInfo.paymentType === 'monthly' ? t('contract.monthly_subscription', 'Monthly Subscription') : t('contract.one_time_payment', 'One-Time Payment')}
                </div>
                <div>
                  <strong>{t('contract.fee', 'Fee')}:</strong> {buyerInfo.paymentType === 'monthly' ? buyerInfo.monthlyFee : buyerInfo.oneTimeFee} {buyerInfo.currency}
                </div>
                <div>
                  <strong>{t('contract.database_provider', 'Database Provider')}:</strong> {t(`contract.db_${buyerInfo.databaseProvider}`, buyerInfo.databaseProvider === 'default' ? 'Default MySQL' : buyerInfo.databaseProvider === 'aiven' ? 'Aiven Cloud SQL' : 'MariaDB')}
                </div>
              </div>
            </div>

            {/* System Actions Info */}
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-3">⚙️ {t('contract.system_actions', 'System Actions')}</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>✓ {t('contract.create_profile', 'Create profile for the domain')}</li>
                <li>✓ {t('contract.create_admin_user', 'Create super admin user for the buyer')}</li>
                <li>✓ {t('contract.assign_permissions', 'Assign full permissions to the admin')}</li>
                <li>✓ {t('contract.send_email', 'Send login credentials via email')}</li>
                <li>✓ {t('contract.send_whatsapp', 'Send credentials via WhatsApp (if provided)')}</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => setStep("buyer-info")}
                disabled={loading}
                className="px-8 py-3 border border-gray-300 rounded-2xl font-bold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('contract.back', 'Back')}
              </button>
              <button
                onClick={handleCreateContract}
                disabled={loading}
                className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {loading ? t('contract.creating', 'Creating...') : t('contract.create', 'Create Contract')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === "success" && createdData && (
          <div className="space-y-8">
            <div className="bg-green-50 rounded-2xl p-8 border border-green-200 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-green-900 mb-2">
                {t('contract.success_title', 'Contract Created Successfully!')}
              </h2>
              <p className="text-green-800">
                {t('contract.success_message', 'Login credentials have been sent to email and WhatsApp')}
              </p>
            </div>

            {/* Success Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Profile Details */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4">📁 {t('contract.profile_created', 'Profile Created')}</h3>
                <div className="text-sm space-y-3 text-gray-700">
                  <div>
                    <strong>{t('contract.profile_id', 'Profile ID')}:</strong>
                    <div className="font-mono text-xs bg-white p-2 rounded mt-1 break-all">{createdData.profile.id}</div>
                  </div>
                  <div>
                    <strong>{t('contract.domain', 'Domain')}:</strong>
                    <div>{createdData.buyerInfo.domainName}</div>
                  </div>
                </div>
              </div>

              {/* User Details */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4">👤 {t('contract.admin_user_created', 'Admin User Created')}</h3>
                <div className="text-sm space-y-3 text-gray-700">
                  <div>
                    <strong>{t('contract.username', 'Username')}:</strong>
                    <div className="font-mono text-xs bg-white p-2 rounded mt-1">{createdData.buyerInfo.buyerEmail.split('@')[0]}</div>
                  </div>
                  <div>
                    <strong>{t('contract.email', 'Email')}:</strong>
                    <div className="font-mono text-xs bg-white p-2 rounded mt-1">{createdData.buyerInfo.buyerEmail}</div>
                  </div>
                  <div>
                    <strong>{t('contract.login_password', 'Login Password')}:</strong>
                    <div className="font-mono text-xs bg-white p-2 rounded mt-1 break-all">{createdData.tempPassword}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Domain Setup Guide */}
            <div className="bg-purple-50 rounded-2xl p-6 border border-purple-200">
              <h3 className="font-bold text-purple-900 mb-3">🌐 {t('contract.domain_setup', 'Domain Setup Guide')}</h3>
              <ol className="text-sm text-purple-800 space-y-2 list-decimal list-inside">
                <li>{t('contract.step_dns', 'Point your domain DNS to Vercel nameservers')}</li>
                <li>{t('contract.step_vercel', 'Add domain in Vercel project settings')}</li>
                <li>{t('contract.step_ssl', 'Wait for SSL certificate to be issued (5-10 minutes)')}</li>
                <li>{t('contract.step_activate', 'Activate the profile in System Settings')}</li>
              </ol>
            </div>

            {/* Next Steps */}
            <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-200">
              <h3 className="font-bold text-yellow-900 mb-3">📋 {t('contract.next_steps', 'Next Steps')}</h3>
              <ol className="text-sm text-yellow-800 space-y-2 list-decimal list-inside">
                <li>{t('contract.buyer_receives', 'Buyer receives login credentials at email/WhatsApp')}</li>
                <li>{t('contract.buyer_login', 'They can log in and configure their store')}</li>
                <li>{t('contract.buyer_domain', 'Configure domain DNS settings')}</li>
                <li>{t('contract.buyer_activate', 'Activate profile when ready')}</li>
                <li>{t('contract.payment_setup', 'Set up payment/billing in your system')}</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <button
                onClick={downloadPDF}
                className="px-8 py-3 border border-indigo-300 bg-indigo-50 text-indigo-700 rounded-2xl font-bold hover:bg-indigo-100 transition-colors"
              >
                📄 {t('contract.download_pdf', 'Download Contract')}
              </button>
              <button
                onClick={handleReset}
                className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-colors"
              >
                {t('contract.create_another', 'Create Another Contract')}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
});

export default ContractAdmin;
